import {
	AfterViewInit,
	ComponentRef,
	Directive,
	ElementRef,
	EmbeddedViewRef,
	Input,
	OnDestroy,
	Renderer2,
	TemplateRef,
	ViewContainerRef
} from '@angular/core';
import { TeaseWallContext } from './tease-wall.context';
import { BehaviorSubject, Observable, Subject, combineLatest, of, timer, isObservable } from 'rxjs';
import { delayWhen, filter, switchMap, takeUntil } from 'rxjs/operators';
import { TeaseWallComponent } from './component/tease-wall.component';
import { Injector } from '@angular/core';
import {
	TEASE_DATA_LOADING,
	TEASE_WALL_BLUR_CLASS,
	TEASE_WALL_BLUR_CLASS_SELECTOR,
	TEASE_WALL_CONFIG,
	TEASE_WALL_HIDDEN,
	TEASE_WALL_INDEX_ATTRIBUTE,
	TEASE_WALL_WRAPPER_CLASS
} from './constants';
import type { TeaseWallConfig } from '@go-modules/directives/tease-wall/tease-wall.config';

@Directive({
	selector: '[teaseWall]',
	standalone: false
})
export class TeaseWallDirective<TData> implements OnDestroy, AfterViewInit {
	private _context: TeaseWallContext<TData>;
	private ofData: Observable<TData>;
	private config: TeaseWallConfig;
	private hideTeaseWall$$ = new BehaviorSubject(false);
	private isLoading$$ = new BehaviorSubject(true);
	private configReady$$ = new Subject();
	private ofDataReady$$ = new Subject();
	private afterViewInit$$ = new Subject<boolean>();
	private directiveDestroyed$$ = new Subject();
	private data: TData;
	private dataChanged = false;
	private teaseWallComponentRef: ComponentRef<TeaseWallComponent>;
	private hideTeaseWallPreviousValue = false;

	constructor (private templ: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private elementRef: ElementRef,
		private renderer2: Renderer2,
		private injector: Injector
	) {

		combineLatest([
			this.configReady$$,
			this.ofDataReady$$,
			this.afterViewInit$$,
			this.hideTeaseWall$$
		])
			.pipe(
				takeUntil(this.directiveDestroyed$$),
				filter(
					([_config, _ofData, afterViewInit, hideTeaseWall]) => {
						// Check if the tease wall already exists to prevent re-subscribing to the same data
						// when toggling tease wall visibility
						if (this.teaseWallComponentRef && this.hideTeaseWallPreviousValue !== hideTeaseWall) {
							this.hideTeaseWallPreviousValue = hideTeaseWall;
							this.isLoading$$.next(false);
							return false;
						}

						return afterViewInit;
					}),
				switchMap(() => {
					this.isLoading$$.next(true);
					// We need to render the view without waiting for the data to be loaded first
					if(!this.data) this.updateView();

					if (this.data && !this.dataChanged) return of(this.data);

					return this.ofData ? this.ofData : of(true);
				})
			)
			.subscribe((data: TData) => {
				this.isLoading$$.next(false);
				this.dataChanged = false;
				this.data = data;
				this.updateView();
			});
	}

	public ngAfterViewInit (): void {
		// If ofData is not supplied to the directive at this point,
		// we can safely assume that the view is ready to be updated
		if (!this.ofData) this.ofDataReady$$.next(true);

		this.afterViewInit$$.next(true);
	}

	public ngOnDestroy (): void {
		this.directiveDestroyed$$.next(true);
		this.directiveDestroyed$$.complete();
		this.configReady$$.complete();
		this.ofDataReady$$.complete();
	}

	@Input()
	public set teaseWallHide (hide: boolean) {
		this.hideTeaseWall$$.next(hide);
	}

	@Input()
	public set teaseWall (config: TeaseWallConfig) {
		this.dataChanged = true;
		this.config = config;
		this.configReady$$.next(true);
	}

	public get teaseWall () {
		return this.config;
	}

	/**
	 * This directive can potentially update the view before the elements we want to blur are rendered (ex: *ngIf)
	 * This "of" input:
	 * 1. Gives the directive a way to know when to update the view
	 *     - Ex: <section *teaseWall="aiMarkerTeaseConfig; of: loading$">
	 * 2. Extract the value from the observable/data so that it can be used in the component that uses this directive
	 *     - Ex: <section *teaseWall="aiMarkerTeaseConfig; let selectedAnalytic of selectedAnalytic$">
	 */
	@Input()
	public set teaseWallOf (data: TData | Observable<TData> | undefined) {
		this.dataChanged = true;
		this.ofData = isObservable(data) ? data : of(data);
		this.ofDataReady$$.next(true);
	}

	private updateView () {
		this._context = new TeaseWallContext(this.data, this.teaseWall);

		// Check and retrieve existing embedded view
		let embeddedViewRef =
			this.viewContainer.length > 0 ?
				this.viewContainer.get(0) as EmbeddedViewRef<TeaseWallContext<TData>> : null;

		// Do not clear the view if it already exists. It will cause visual UI flickers and other unexpected behaviors
		// depending on the component this directive is attached to.
		if (!embeddedViewRef) {
			embeddedViewRef = this.viewContainer.createEmbeddedView(this.templ, this._context);
		} else {
			embeddedViewRef.context = {...this._context};
		}

		// the element the directive is attached to
		const viewRootElement = embeddedViewRef.rootNodes[0];

		if (!this.config.useRealData && !this.teaseWallComponentRef) {
			this.teaseWallComponentRef = this.viewContainer.createComponent(TeaseWallComponent, {
				injector: this.createInjector()
			});

			// Insert tease wall as first/last child element to have control over accessibility tab behavior
			if (this.config?.insertAfterChildren === true) {
				this.renderer2.appendChild(
					viewRootElement,
					this.teaseWallComponentRef.instance.elementRef.nativeElement
				);
			} else {
				this.renderer2.insertBefore(
					viewRootElement,
					this.teaseWallComponentRef.instance.elementRef.nativeElement,
					viewRootElement.firstElementChild
				);
			}
			this.teaseWallComponentRef.changeDetectorRef.detectChanges();
		} else if (!this.config.useRealData && this.teaseWallComponentRef) {
			// If tease wall already created, but we want to show another tease wall based on the updated config
			// Ex: Purchase -> Upgrade Tease Wall Transition
			this.teaseWallComponentRef.instance.config = this.config;
			this.teaseWallComponentRef.instance.initCurrentConfig();
			this.teaseWallComponentRef.changeDetectorRef.detectChanges();
		}

		// In the case that there are multiple tease walls, and they are being
		// removed, the embeddedViewRef may have already been destroyed
		if (!embeddedViewRef.destroyed) {
			embeddedViewRef.detectChanges();
			this.applyClass(viewRootElement);
		}
	}

	private applyClass (viewRootElement: HTMLElement) {
		const selector = this.config.select.join(', ');
		if (!this.config.select.length) return;

		if (this.config.useRealData) {
			viewRootElement.classList.remove(TEASE_WALL_WRAPPER_CLASS);
			viewRootElement
				.querySelectorAll(TEASE_WALL_BLUR_CLASS_SELECTOR)
				.forEach((element: HTMLElement) => {
					element.classList.remove(TEASE_WALL_BLUR_CLASS);
					const index = element.getAttribute(TEASE_WALL_INDEX_ATTRIBUTE);
					if (index) {
						element.tabIndex = +index;
					}
				});

			// If switching from fakeData to realData, remove the tease-wall
			if (this.teaseWallComponentRef) {
				this.renderer2.removeChild(viewRootElement, this.teaseWallComponentRef.location.nativeElement);
				this.teaseWallComponentRef.destroy();
				this.teaseWallComponentRef = null;
			}
			return;
		}

		viewRootElement.classList.add(TEASE_WALL_WRAPPER_CLASS);
		viewRootElement
			.querySelectorAll(selector)
			.forEach((element: HTMLElement) => {
				element.classList.add(TEASE_WALL_BLUR_CLASS);
				if (element.tabIndex > -1) {
					element.setAttribute(TEASE_WALL_INDEX_ATTRIBUTE, element.tabIndex.toString());
				}
				element.tabIndex = -1;
			});
	}

	private createInjector (): Injector {
		return Injector.create({
			parent: this.injector,
			providers: [{
				provide: TEASE_WALL_CONFIG,
				useValue: this.config
			},
			{
				provide: TEASE_DATA_LOADING,
				useValue: this.isLoading$$.asObservable()
			},
			{
				provide: TEASE_WALL_HIDDEN,
				useValue: this.hideTeaseWall$$.asObservable()
					.pipe(delayWhen((hide) => {
						return this.config.showDelay && !hide ? timer(this.config.showDelay) : timer(0);
					}))
			}]
		});
	}
}
